import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export interface LeaveRecord {
  allocated?: number;
  accrued?: number;
  used: number;
  pending: number;
  balance?: number;
  gracePeriodUsed?: number;
}

export interface LeaveBalance {
  _id: string;
  user: string;
  org: string;
  year: number;
  annualLeave: LeaveRecord;
  sickLeave: LeaveRecord;
  pressingNecessity: LeaveRecord;
  compassionate: LeaveRecord;
  bereavement: LeaveRecord;
  juryDuty: LeaveRecord;
  election: LeaveRecord;
  bandBusiness: LeaveRecord;
  governance: LeaveRecord;
  education: LeaveRecord;
  professionalDevelopment: LeaveRecord;
  culturalSpiritual: LeaveRecord;
  leaveWithoutPay: LeaveRecord;
  lastUpdated?: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export type LeaveBalanceResponse = {
  success: boolean;
  message?: string;
  data: LeaveBalance;
};

const fetchLeaveBalance = async (userId?: string): Promise<LeaveBalanceResponse> => {
  const url = userId ? `/leaves/balance/${userId}` : `/leaves/balance`;
  return (await customAxios.get<LeaveBalanceResponse>(url)).data;
};

export const useGetLeaveBalance = (userId?: string) =>
  useQuery<LeaveBalanceResponse, AxiosError>(
    ["leaves", "balance", userId || "me"],
    () => fetchLeaveBalance(userId),
    {
      onError: (error) =>
        showNotification({
          title: "Error Loading Leave Balance",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to load leave balance",
          color: "red",
        }),
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: 1,
      refetchOnMount: "always",
    }
  );
